import React, { LegacyRef, useContext, useState } from 'react';
import t from 'react-translate';
import { useDispatch, useSelector } from 'react-redux';
import { css } from '@emotion/core';

// redux
import { wrapThunkAction } from 'redux/utils';
import { deleteTableColumn } from 'redux/actions/quizzes';
import { getFlatCourseAliases } from 'redux/selectors/course';

import NvTooltip from 'shared/components/nv-tooltip';
import QuizContext, { SavingIndicator } from 'quizzes/components/context';
import NvIcon from 'shared/components/nv-icon';
import { halfSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import { gray4, gray6, primary, white } from 'styles/global_defaults/colors';
import { config } from '../../../../../config/config.json';

type DeleteColumnProps = {
  deleteColumnRef: LegacyRef<HTMLButtonElement>
  setShowButtons: Function;
  childQuestions: number[][];
  setChildQuestions: Function;
  getTableArray: Function;
  columnIndex: number;
  isDeleteColumnDisabled: boolean;
  setIsDeleteColumnDisabled: Function;
};
const DeleteColumnButton = (props: DeleteColumnProps) => {
  const {
    deleteColumnRef,
    setShowButtons,
    childQuestions,
    setChildQuestions,
    getTableArray,
    columnIndex,
    isDeleteColumnDisabled,
    setIsDeleteColumnDisabled,
  } = props;
  const {
    currentQuestion,
    savingIndicatorTimeoutRef,
    setSavingStatus,
    canMakeStructuralChanges,
  } = useContext(QuizContext);
  const { numRows, numColumns, childrenQuestions } = currentQuestion;
  const isMinColumnLimitReached = childQuestions[0].length === 1;
  const isButtonDisabled = isDeleteColumnDisabled
    || isMinColumnLimitReached
    || !canMakeStructuralChanges;

  const dispatch = useDispatch();
  const courseAliases = useSelector(getFlatCourseAliases);

  const [deleteColumnHovered, setDeleteColumnHovered] = useState(false);

  const buttonStyles = css`
    &.delete-column-button {
      position: absolute;
      transform: translateY(calc(-100% - 5px));
      border-radius: ${halfSpacing}px;
      height: ${halfSpacing}px;
      background-color: ${isButtonDisabled ? gray6 : `${primary}33`};
      border: none;
      color: ${isButtonDisabled ? gray4 : primary};
    }
    &.delete-column-button:hover {
      background-color: ${isButtonDisabled ? gray6 : primary};
      height: ${isButtonDisabled ? halfSpacing : standardSpacing}px;
      color: ${isButtonDisabled ? gray4 : white};
    }
  `;

  const handleDeleteColumn = () => {
    const newQuestions = childQuestions;
    let deletedChildQuestions = [];
    childQuestions.forEach((eachRow, i) => {
      const questions = newQuestions[i].splice(columnIndex, 1);
      deletedChildQuestions = [...deletedChildQuestions, ...questions];
    });

    setChildQuestions([...newQuestions]);
    setShowButtons(false);
    setDeleteColumnHovered(false);

    setIsDeleteColumnDisabled(true);
    setSavingStatus(SavingIndicator.SAVING_STATUS);
    clearTimeout(savingIndicatorTimeoutRef.current);
    wrapThunkAction(dispatch(deleteTableColumn(
      {
        questionId: currentQuestion.id,
        columnIndex,
        deletedChildQuestions,
      },
    ))).then(() => {
      setSavingStatus(SavingIndicator.SUCCESS_STATUS);
    }).catch(() => {
      setSavingStatus(SavingIndicator.ERROR_STATUS);
      setChildQuestions(getTableArray(numRows, numColumns, childrenQuestions));
    }).finally(() => {
      setIsDeleteColumnDisabled(false);
      savingIndicatorTimeoutRef.current = setTimeout(() => {
        setSavingStatus(SavingIndicator.HIDDEN_STATUS);
      }, 2000);
    });
  };

  const getTooltipText = () => {
    if (!canMakeStructuralChanges) {
      return t.QUIZZES.TABLE_QUESTION.DISABLED_TOOLTIP(courseAliases);
    }
    if (isMinColumnLimitReached) {
      return t.QUIZZES.TABLE_QUESTION.MIN_COLUMN_REACHED();
    }

    return t.QUIZZES.TABLE_QUESTION.DELETE_COLUMN();
  };

  return (
    <NvTooltip
      text={getTooltipText()}
    >
      <button
        css={buttonStyles}
        type='button'
        onMouseEnter={() => setDeleteColumnHovered(true)}
        onMouseLeave={() => setDeleteColumnHovered(false)}
        onClick={handleDeleteColumn}
        disabled={isButtonDisabled}
        ref={deleteColumnRef}
        className='delete-column-button d-flex align-items-center justify-content-center'
        data-qa={config.pendo.activities.quiz.questionModal.tableQuestion.deleteColumn}
      >
        <NvIcon
          size='xs-smallest'
          icon={`${(deleteColumnHovered && !isMinColumnLimitReached) ? 'trash' : 'dash'}`}
        />
      </button>
    </NvTooltip>
  );
};

export default DeleteColumnButton;
